<template>
  <div ref="host" class="svg-map" />
</template>

<script setup lang="ts">
import { ref, watch, onMounted, useTemplateRef, type Ref } from "vue";
import { uuid } from "@/utils/uuid";
import { sanitizeSvg } from "@/utils/svg";

interface Props {
  source: string;
  /** Id of the highlighted shape, linked to either a Sector or a Section. */
  selectedSector?: string | null;
  locked?: boolean;
}

const {
  source,
  selectedSector = null,
  locked = false,
} = defineProps<Props>();

const emit = defineEmits<{
  setup: [elements: SetupElement[]];
  /** Id of the clicked shape, linked to either a Sector or a Section. */
  click: [shapeId: string];
}>();

defineOptions({
  name: "SvgMap",
});

/** Elements that draw something, a group only holds them. */
const SHAPES = "path, polygon, polyline, rect, circle, ellipse";

const host = useTemplateRef<HTMLDivElement>("host");

const sectorSVGElements: Ref<Element[]> = ref([]);
const disableds: Ref<Set<string>> = ref(new Set());

function download(src: string): Promise<string> {
  let request = downloads.get(src);

  if (request === undefined) {
    request = fetch(src, { credentials: "omit" })
      .then((response) => {
        if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
        return response.text();
      })
      .catch((error: unknown) => {
        downloads.delete(src);
        throw error;
      });

    downloads.set(src, request);
  }

  return request;
}

async function load(src: string): Promise<void> {
  let markup: string;

  try {
    markup = await download(src);
  } catch (error) {
    console.error(`SvgMap: failed to load "${src}"`, error);
    return;
  }

  // The map is uploaded per event and served from outside the application, so it is
  // sanitized before it ever touches the document
  const { element, removed } = sanitizeSvg(markup);

  if (removed > 0) {
    console.error(`SvgMap: blocked ${removed} unsafe node(s)/attribute(s) in "${src}"`);
  }

  if (element === null) {
    console.error(`SvgMap: "${src}" is not a valid SVG`);
    return;
  }

  // A newer source won the race while this one was downloading
  if (src !== source || host.value === null) return;

  element.setAttribute("width", "100%");
  element.setAttribute("height", "100%");

  host.value.replaceChildren(element);
  setup(element);
}

function area(shape: Element): number {
  try {
    const box = (shape as SVGGraphicsElement).getBBox();
    return box.width * box.height;
  } catch {
    // Some browsers throw for a shape that isn't rendered, e.g. on a hidden step
    return 0;
  }
}

/**
 * Shape whose fill is the element's color. A group has no fill of its own, so it's the largest shape
 * drawn inside it, the first one can be the outlined letters of a caption.
 */
function paintedShape(element: Element): Element {
  if (element.matches(SHAPES)) return element;

  let largest: Element = element;
  let largestArea = -1;

  for (const shape of element.querySelectorAll(SHAPES)) {
    const shapeArea = area(shape);

    // Ties keep the first one, which is all there is when nothing could be measured
    if (shapeArea > largestArea) {
      largest = shape;
      largestArea = shapeArea;
    }
  }

  return largest;
}

function setup(svg: SVGSVGElement): void {
  const gRoot = svg.querySelector("#root");

  if (gRoot === null) return;

  sectorSVGElements.value = Array.from(gRoot.querySelectorAll("*[id^='_']"));

  // Builds background root for  the shadow effect on selection
  const background = gRoot.cloneNode(true) as SVGGElement;
  background.id = `background-${background.id}`;
  gRoot.parentElement?.insertBefore(background, gRoot);

  const elements: SetupElement[] = [];

  for (const sector of sectorSVGElements.value) {
    if (sector.id.startsWith("_")) {
      // removes _
      sector.id = sector.id.slice(1);
    }

    let element: SVGElement = sector as SVGElement;

    // If for any reason the current element's ID doesn't match a valid UUID
    // we still try to look for a replacement in it's children
    if (!uuid.validate(sector.id)) {
      const replacement = [ ...sector.children ].find(c => uuid.validate(c.id));

      if (replacement === undefined) {
        continue;
      }

      element = replacement as SVGElement;
    }

    element.style.transition = "300ms ease";

    elements.push({
      element: sector,
      color: getComputedStyle(paintedShape(element)).fill,
    });

    (sector as HTMLElement).style.cursor = "pointer";

    // A Section's shape can be nested inside its Sector's shape, only the innermost one is clicked
    sector.addEventListener("click", (event) => {
      event.stopPropagation();
      onClick(sector);
    });
  }

  emit("setup", elements);
  updateSelection(selectedSector);
}

function onClick(sector: Element): void {
  if (locked) return;
  emit("click", sector.id);
}

function getPathFromSectorElement(sector: Element): Element | undefined {
  return [ ...sector.children ].find(e => e.tagName === "path");
}

function updateSelection(sectorId: string | null): void {
  sectorSVGElements.value.forEach((e) => {
    if (e.id === sectorId) {
      e.classList.toggle("selected");
    } else {
      e.classList.remove("selected");
    }
  });
}

function disable(id: string): void {
  disableds.value.add(id);
  updateDisableds();
}

function enable(id: string): void {
  disableds.value.delete(id);
  updateDisableds();
}

function updateDisableds(): void {
  sectorSVGElements.value.forEach(e => e.classList.remove("disabled"));
  sectorSVGElements.value.filter(e => disableds.value.has(e.id)).forEach(e => e.classList.add("disabled"));
}

onMounted(() => {
  void load(source);
});

// Watch source prop changes
watch(
  () => source,
  (newVal) => {
    void load(newVal);
  },
);

// Watch selectedSector prop changes
watch(
  () => selectedSector,
  (newVal) => {
    updateSelection(newVal);
  },
  { immediate: true },
);

// Expose methods that parent components may need
defineExpose({
  disable,
  enable,
  getPathFromSectorElement,
  updateSelection,
  updateDisableds,
});
</script>

<script lang="ts">
/** Maps are shared between the sector and the seat steps, so the download is cached. */
const downloads = new Map<string, Promise<string>>();

/** A shape of the map, with the fill it's drawn with. */
export interface SetupElement {
  element: Element;
  color: string;
}

// Export interface for component ref typing in parent components
export interface SvgMapExposed {
  disable: (id: string) => void;
  enable: (id: string) => void;
  getPathFromSectorElement: (sector: Element) => Element | undefined;
  updateSelection: (sectorId: string | null) => void;
  updateDisableds: () => void;
}
</script>

<style>
.svg-map svg .selected {
  opacity: 1 !important;
  filter: drop-shadow(0px 0px 100px #222);
  stroke: black;
  stroke-opacity: 0.3;
  stroke-width: 4px;
}

/* A disabled shape can be a polygon with no children */
.svg-map svg .disabled,
.svg-map svg .disabled * {
  fill: #bbbbbb;
  opacity: 1 !important;
  cursor: default !important;
}
</style>
